import { memo, useMemo, useTransition } from "react"
import type { FC, ReactElement, TransitionStartFunction } from "react"

import { yupResolver } from "@hookform/resolvers/yup"
import { motion, useReducedMotion } from "framer-motion"
import map from "lodash-es/map"
import { type UseFormReturn, useForm } from "react-hook-form"
import type { ObjectSchema } from "yup"

import {
    Button,
    EButtonVariant,
    ETypographyAlignment,
    ETypographyColor,
    ETypographyFamily,
    ETypographySize,
    ETypographyTag,
    ETypographyWeight,
    Input,
    type TInputProps,
    Typography
} from "@/3514/components"
import { propsAreEqual } from "@/3514/utils"
import { type IUseTranslation, useTranslation } from "@/hooks"
import { type TEmptyAsyncCallback, TEmptyRenderCallback } from "@/shared/types/functions"

import { getAuthSchemaConfig } from "./auth.form.schema"
import { authUiConfig } from "./auth.ui.config"
import type { TAuthContainerMode, TAuthFormRenderInputsPropsFunction, TAuthFormValues } from "./auth.ui.types"

type _TProps = {
    mode: TAuthContainerMode
    renderSubtitle: TEmptyRenderCallback
    renderInputsProps: TAuthFormRenderInputsPropsFunction
    renderFormPassResetHint: TEmptyRenderCallback
    onSubmit(values: TAuthFormValues): Promise<void>
}

const classes = {
    section: (): string => "max-md:px-[10px] md:max-w-[500px] w-full [display:grid] gap-[20px] md:gap-[30px]",
    heading: (): string => "[display:grid] gap-[5px]",
    form: (): string => "[display:grid] gap-[20px] md:gap-[30px]",
    formContent: (): string => "[display:grid] gap-[10px]",
    inputs: (): string => "[display:grid] gap-[5px]"
} as const

const AuthContainer: FC<_TProps> = ({
    mode,
    renderSubtitle,
    renderInputsProps,
    renderFormPassResetHint,
    onSubmit
}: _TProps): ReactElement => {
    const { t }: IUseTranslation = useTranslation()

    const isReducedMotion: boolean = useReducedMotion()

    const [isSubmittingTransition, startSubmittingTransition]: [boolean, TransitionStartFunction] = useTransition()

    const schema: ObjectSchema = useMemo((): ObjectSchema => getAuthSchemaConfig({ t })[mode], [t, mode])

    const {
        register: registerField,
        trigger: triggerField,
        handleSubmit: handleFormSubmit,
        formState: { errors: formErrors, isValid: isFormValid }
    }: UseFormReturn<TAuthFormValues> = useForm<TAuthFormValues>({
        resolver: yupResolver(schema, { abortEarly: false }),
        defaultValues: authUiConfig.formDefaultValues[mode],
        progressive: true,
        shouldFocusError: true,
        shouldUnregister: false,
        reValidateMode: "onBlur",
        mode: "onTouched",
        criteriaMode: "all"
    })

    const submitHandler: TEmptyAsyncCallback = handleFormSubmit((values: TAuthFormValues): void =>
        startSubmittingTransition(async (): Promise<void> => {
            try {
                await onSubmit(values)
            } catch (e) {
                void 0
            }
        })
    )

    return (
        <motion.section
            className={classes.section()}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: isReducedMotion ? 0 : 0.35, ease: "easeOut" }}
        >
            <div className={classes.heading()}>
                <Typography
                    text={t(authUiConfig.titlesMap[mode])}
                    alignment={ETypographyAlignment.Center}
                    tag={ETypographyTag.Heading1}
                    color={ETypographyColor.Black}
                    weight={ETypographyWeight.Medium}
                    size={ETypographySize.Heading1}
                    family={ETypographyFamily.DmSerif}
                />

                {renderSubtitle()}
            </div>

            <form onSubmit={submitHandler} className={classes.form()}>
                <div className={classes.formContent()}>
                    <div className={classes.inputs()}>
                        {map<TInputProps, ReactElement>(
                            renderInputsProps({
                                register: registerField,
                                trigger: triggerField,
                                errors: formErrors,
                                isSubmitting: isSubmittingTransition
                            }),
                            (props: TInputProps): ReactElement => (
                                <Input key={props.name} {...props} />
                            )
                        )}
                    </div>

                    {renderFormPassResetHint()}
                </div>

                <Button
                    type="submit"
                    isCentered
                    isDisabled={!isFormValid}
                    isBusy={isSubmittingTransition}
                    variant={EButtonVariant.Solid}
                >
                    {t(authUiConfig.butonLabelsMap[mode])}
                </Button>
            </form>
        </motion.section>
    )
}

AuthContainer.displayName = "AuthContainer"

const MemoizedAuthContainer: FC<_TProps> = memo(AuthContainer, propsAreEqual)

export { MemoizedAuthContainer as AuthContainer }
